/**
 * Aba "Jurisprudência" (admin/gerente) — base local de jurisprudência (juris.db)
 * + inclusão MANUAL de decisões fora do kanban.
 *
 * Fluxo: subir PDF (ou colar texto) → /extrair (auto-metadados via Haiku) →
 * conferir/corrigir + escolher ESCOPO (publico/cliente/segredo) → /incluir
 * (grava nota .md no Vault se publico + upsert no juris.db). Trava LGPD: só
 * 'publico' é citável; 'cliente'/'segredo' ficam fora do que vira precedente.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { TopNav } from '../components/TopNav';
import { api, ApiError } from '../api';
import type { FalcaoItem, JurisMeta, JurisStats, JurisResultado } from '../api';

const ESCOPOS: { v: 'publico' | 'cliente' | 'segredo'; label: string; hint: string }[] = [
  { v: 'publico', label: 'Público (citável)', hint: 'Jurisprudência pública — vira precedente citável e nota no vault.' },
  { v: 'cliente', label: 'Cliente (interno)', hint: 'Decisão do próprio cliente — entra na base p/ análise interna, NÃO citável em peça de outro caso.' },
  { v: 'segredo', label: 'Segredo de justiça', hint: 'Não indexado como precedente; sigilo preservado.' },
];

const FAVS = ['favoravel', 'desfavoravel', 'indefinido'];

function Campo({ label, value, onChange, full }: { label: string; value: string; onChange: (v: string) => void; full?: boolean }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? 'sm:col-span-2' : ''}`}>
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="text-sm px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-400" />
    </label>
  );
}

export function Jurisprudencia() {
  const [stats, setStats] = useState<JurisStats | null>(null);
  const [erro, setErro] = useState('');
  const [okMsg, setOkMsg] = useState('');

  // inclusão
  const [texto, setTexto] = useState('');
  const [extraindo, setExtraindo] = useState(false);
  const [incluindo, setIncluindo] = useState(false);
  const [draft, setDraft] = useState<JurisMeta | null>(null);
  const [draftTexto, setDraftTexto] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // busca
  const [q, setQ] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultados, setResultados] = useState<JurisResultado[]>([]);

  // consulta ao Falcão (curadoria → base do tenant)
  const [fq, setFq] = useState('');
  const [fTribunal, setFTribunal] = useState('');
  const [fTribunais, setFTribunais] = useState<{ sigla: string; nome: string }[]>([]);
  const [fBuscando, setFBuscando] = useState(false);
  const [fIncluindo, setFIncluindo] = useState(false);
  const [fResultados, setFResultados] = useState<FalcaoItem[]>([]);
  const [fSel, setFSel] = useState<Set<string>>(new Set());
  const [fAberto, setFAberto] = useState<Set<string>>(new Set());
  const fSelecionadas = useMemo(() => fResultados.filter((r) => fSel.has(r.id)), [fResultados, fSel]);

  function carregarStats() { api.jurisStats().then(setStats).catch(() => {}); }
  useEffect(carregarStats, []);
  useEffect(() => { api.falcaoTribunais().then((d) => setFTribunais(d.tribunais)).catch(() => {}); }, []);

  function msgErro(e: unknown) {
    setErro(e instanceof ApiError ? e.body.slice(0, 200) : String(e));
    setOkMsg('');
  }

  async function extrair() {
    setErro(''); setOkMsg(''); setExtraindo(true); setDraft(null);
    try {
      const f = fileRef.current?.files?.[0] ?? null;
      if (!f && texto.trim().length < 50) { setErro('Suba um PDF ou cole o texto da decisão (≥50 caracteres).'); return; }
      const r = await api.jurisExtrair(f, f ? '' : texto);
      setDraft({ ...r.metadados, escopo: 'publico' });
      setDraftTexto(r.texto);
    } catch (e) { msgErro(e); } finally { setExtraindo(false); }
  }

  async function incluir() {
    if (!draft) return;
    setErro(''); setOkMsg(''); setIncluindo(true);
    try {
      const r = await api.jurisIncluir(draft, draftTexto);
      setOkMsg(`Incluído (id ${r.id}, ${r.escopo})${r.citavel ? ' — citável' : ' — não citável'}.${r.nota_vault ? ' Nota gravada no vault.' : ''}`);
      setDraft(null); setDraftTexto(''); setTexto('');
      if (fileRef.current) fileRef.current.value = '';
      carregarStats();
    } catch (e) { msgErro(e); } finally { setIncluindo(false); }
  }

  async function buscar(e?: React.FormEvent) {
    e?.preventDefault();
    setBuscando(true); setErro('');
    try { setResultados((await api.jurisBuscar(q)).resultados); }
    catch (er) { msgErro(er); } finally { setBuscando(false); }
  }

  async function pesquisarFalcao(e?: React.FormEvent) {
    e?.preventDefault();
    if (fq.trim().length < 3) { setErro('Digite ao menos 3 caracteres para pesquisar no Falcão.'); return; }
    setFBuscando(true); setErro(''); setOkMsg(''); setFSel(new Set()); setFAberto(new Set());
    try { setFResultados((await api.falcaoSearch(fq.trim(), fTribunal || undefined)).resultados); }
    catch (er) {
      setFResultados([]);
      setErro(er instanceof ApiError && er.status === 503
        ? 'Falcão temporariamente indisponível (bloqueio). Tente de novo em instantes.'
        : er instanceof ApiError ? er.body.slice(0, 200) : String(er));
    } finally { setFBuscando(false); }
  }

  async function incluirDoFalcao() {
    if (!fSelecionadas.length) return;
    setFIncluindo(true); setErro(''); setOkMsg('');
    try {
      const r = await api.jurisFalcaoIncluir(fSelecionadas);
      setOkMsg(`${r.total} ementa(s) do Falcão incluída(s) na base do escritório (escopo público)`
        + (r.erros.length ? ` — ${r.erros.length} com erro.` : '.'));
      if (r.erros.length) setErro(r.erros.slice(0, 3).join(' | '));
      setFSel(new Set());
      carregarStats();
    } catch (er) { msgErro(er); } finally { setFIncluindo(false); }
  }

  function fToggle(id: string) {
    setFSel((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function fToggleTeor(id: string) {
    setFAberto((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  const set = (k: keyof JurisMeta) => (v: string) => setDraft((d) => (d ? { ...d, [k]: v } : d));

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <TopNav />
      <main className="max-w-screen-lg mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-xl font-display font-bold text-slate-800 dark:text-slate-100">📚 Base de Jurisprudência</h1>
          {stats && (
            <div className="text-xs text-slate-500 dark:text-slate-400 flex gap-3 flex-wrap">
              <span><strong className="text-slate-700 dark:text-slate-200">{stats.total}</strong> acórdãos</span>
              <span>com turma: {stats.com_turma}</span>
              {Object.entries(stats.por_tribunal).map(([t, n]) => <span key={t}>{t}: {n}</span>)}
            </div>
          )}
        </div>

        {erro && <div className="text-sm rounded-lg px-3 py-2 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-900/40">{erro}</div>}
        {okMsg && <div className="text-sm rounded-lg px-3 py-2 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-900/40">{okMsg}</div>}

        {/* ── Incluir decisão ── */}
        <section className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 space-y-3">
          <h2 className="font-semibold text-slate-700 dark:text-slate-200">Incluir decisão (acórdão / sentença)</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">Sobe a decisão fora do fluxo de prazos. Os metadados são extraídos automaticamente; você confere antes de gravar.</p>
          {!draft && (
            <>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <input ref={fileRef} type="file" accept=".pdf,.txt"
                    className="block w-full text-sm text-slate-600 dark:text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900/30 dark:file:text-blue-300 hover:file:bg-blue-100" />
                  <p className="text-[11px] text-slate-400 mt-1">PDF do acórdão/sentença (com camada de texto; escaneado passa por OCR).</p>
                </div>
                <span className="text-xs text-slate-400 self-center">ou</span>
                <textarea value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Colar o texto da decisão…" rows={3}
                  className="flex-1 text-sm px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-400" />
              </div>
              <button onClick={extrair} disabled={extraindo}
                className="text-sm px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50">
                {extraindo ? '⏳ Extraindo metadados…' : 'Extrair metadados'}
              </button>
            </>
          )}

          {/* Confirmação dos metadados auto-extraídos */}
          {draft && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Campo label="Tribunal" value={draft.tribunal} onChange={set('tribunal')} />
                <Campo label="Órgão julgador / turma" value={draft.orgao_julgador} onChange={set('orgao_julgador')} />
                <Campo label="Classe" value={draft.classe} onChange={set('classe')} />
                <Campo label="Nº do processo (CNJ)" value={draft.numero_processo} onChange={set('numero_processo')} />
                <Campo label="Relator" value={draft.relator} onChange={set('relator')} />
                <Campo label="Data de julgamento" value={draft.data_julgamento} onChange={set('data_julgamento')} />
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Ementa</span>
                <textarea value={draft.ementa} onChange={(e) => set('ementa')(e.target.value)} rows={6}
                  className="text-sm px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-400" />
              </label>
              <div className="flex flex-wrap items-end gap-4">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Favorabilidade (ao trabalhador)</span>
                  <select value={draft.favorabilidade} onChange={(e) => set('favorabilidade')(e.target.value)}
                    className="text-sm px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 dark:text-slate-100">
                    {FAVS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </label>
              </div>
              {/* Escopo (LGPD) */}
              <fieldset className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                <legend className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 px-1">Escopo (LGPD / sigilo)</legend>
                <div className="space-y-1.5">
                  {ESCOPOS.map((o) => (
                    <label key={o.v} className="flex items-start gap-2 text-sm cursor-pointer">
                      <input type="radio" name="escopo" checked={draft.escopo === o.v} onChange={() => set('escopo')(o.v)} className="mt-1" />
                      <span><strong className="text-slate-700 dark:text-slate-200">{o.label}</strong> <span className="text-slate-500 dark:text-slate-400">— {o.hint}</span></span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="flex gap-2">
                <button onClick={incluir} disabled={incluindo}
                  className="text-sm px-4 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-50">
                  {incluindo ? '⏳ Gravando…' : 'Confirmar e incluir'}
                </button>
                <button onClick={() => setDraft(null)} className="text-sm px-4 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ── Consultar Falcão (curadoria → base do escritório) ── */}
        <section className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-semibold text-slate-700 dark:text-slate-200">⚖️ Consultar Falcão (jurisprudência oficial JT)</h2>
            {fSelecionadas.length > 0 && (
              <button onClick={incluirDoFalcao} disabled={fIncluindo}
                className="text-sm px-4 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-50">
                {fIncluindo ? '⏳ Incluindo…' : `Incluir ${fSelecionadas.length} selecionada(s) na base`}
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Pesquise no Falcão, selecione as ementas relevantes e inclua na base curada do escritório
            (escopo <strong>público</strong> — vira precedente citável + nota no vault).
          </p>
          <form onSubmit={pesquisarFalcao} className="flex gap-2 flex-wrap">
            <select value={fTribunal} onChange={(e) => setFTribunal(e.target.value)} title="Filtrar por tribunal"
              className="text-sm px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 dark:text-slate-100">
              <option value="">Todos tribunais</option>
              {fTribunais.map((t) => <option key={t.sigla} value={t.sigla}>{t.sigla}</option>)}
            </select>
            <input value={fq} onChange={(e) => setFq(e.target.value)} placeholder='ex.: "horas extras comércio", "adicional de insalubridade"…'
              className="flex-1 min-w-[180px] text-sm px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <button type="submit" disabled={fBuscando || fq.trim().length < 3}
              className="text-sm px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50">
              {fBuscando ? '…' : 'Pesquisar'}
            </button>
          </form>
          <div className="space-y-2">
            {!fBuscando && fResultados.length === 0 && (
              <p className="text-xs text-slate-400 italic">Nenhuma pesquisa ainda. Os resultados aparecem aqui com checkbox para seleção.</p>
            )}
            {fResultados.map((r) => {
              const marcada = fSel.has(r.id);
              const aberto = fAberto.has(r.id);
              const ementa = (r.ementa || r.snippet || '').replace(/==/g, '');
              return (
                <div key={r.id} className={`rounded-lg border transition ${marcada
                  ? 'bg-green-50 dark:bg-green-950/20 border-green-400/60'
                  : 'bg-slate-50 dark:bg-slate-950/40 border-slate-200 dark:border-slate-800'}`}>
                  <label className="flex items-start gap-2.5 p-2.5 cursor-pointer">
                    <input type="checkbox" checked={marcada} onChange={() => fToggle(r.id)} className="mt-1 accent-green-600" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500 dark:text-slate-400">
                        <span className="font-bold text-blue-700 dark:text-blue-300">{r.tribunal}</span>
                        <span className="font-mono">{r.numero_processo || r.numero}</span>
                        {(r.data_julgamento || r.data) && <span>· {r.data_julgamento || r.data}</span>}
                        {r.relator && <span>· Rel. {r.relator}</span>}
                        {r.orgao_julgador && <span>· {r.orgao_julgador}</span>}
                        {!r.possui_ementa && <span className="italic">· sem ementa formal</span>}
                      </div>
                      <p className={`text-sm text-slate-700 dark:text-slate-200 mt-1 ${aberto ? '' : 'line-clamp-3'}`}>
                        {ementa || <span className="italic text-slate-400">(sem texto)</span>}
                      </p>
                    </div>
                  </label>
                  <div className="flex items-center gap-3 px-2.5 pb-2 pl-9">
                    {ementa.length > 200 && (
                      <button type="button" onClick={() => fToggleTeor(r.id)}
                        className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline">
                        {aberto ? 'recolher ▲' : 'expandir ▾'}
                      </button>
                    )}
                    {(r.url || r.url_inteiro_teor) && (
                      <a href={r.url || r.url_inteiro_teor} target="_blank" rel="noreferrer"
                        className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline">abrir no Falcão ↗</a>
                    )}
                  </div>
                </div>
              );
            })}
            {fSelecionadas.length > 0 && (
              <button onClick={incluirDoFalcao} disabled={fIncluindo}
                className="text-sm px-4 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-50">
                {fIncluindo ? '⏳ Incluindo…' : `Incluir ${fSelecionadas.length} selecionada(s) na base`}
              </button>
            )}
          </div>
        </section>

        {/* ── Buscar na base ── */}
        <section className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 space-y-3">
          <h2 className="font-semibold text-slate-700 dark:text-slate-200">Buscar na base</h2>
          <form onSubmit={buscar} className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ex.: adicional de risco, intervalo intrajornada…"
              className="flex-1 text-sm px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <button type="submit" disabled={buscando} className="text-sm px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50">
              {buscando ? '…' : 'Buscar'}
            </button>
          </form>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {resultados.map((r) => (
              <li key={r.id} className="py-2.5 text-sm">
                <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500 dark:text-slate-400">
                  <span className="font-mono">{r.numero_processo}</span>
                  <span>· {r.tribunal}{r.turma ? ` · ${r.turma}` : ''}</span>
                  {r.relator && <span>· Rel. {r.relator}</span>}
                  {r.favorabilidade && r.favorabilidade !== 'indefinido' && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.favorabilidade === 'favoravel' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>{r.favorabilidade}</span>
                  )}
                </div>
                <p className="text-slate-700 dark:text-slate-300 mt-0.5 line-clamp-3">{r.ementa}</p>
              </li>
            ))}
            {!buscando && q && resultados.length === 0 && <li className="py-3 text-sm text-slate-400">Nenhum resultado.</li>}
          </ul>
        </section>
      </main>
    </div>
  );
}
